import {
  RowDataObject,
  PrimitiveValueType,
  RowData,
  FilterFieldValueType,
  FilterFieldType,
  RowDataValueType,
  RowDataReducerAction,
  SetupBaseRequestInfo,
  DeletedObjectsList,
} from "../../../types";
import {
  METADATA_COLUMNS,
  ROW_DATA_KEYS,
  SCHEMA_TITLES,
  TRACK_RECORD_DATA_STATE_KEYS,
} from "../constants";

export const isNewRecord = (rowData: RowData): boolean => {
  return (
    rowData?.created_by === undefined &&
    rowData?.[TRACK_RECORD_DATA_STATE_KEYS.TRACK_EXISTING_CREATED_BY] ===
      undefined
  );
};

export const getUpdatedRowDataIfTabsExist = (
  rowData: RowData,
  selectedDashboardTabSectionKey?: string,
): RowData => {
  if (!selectedDashboardTabSectionKey) return rowData ?? {};

  return (rowData?.[selectedDashboardTabSectionKey] as RowData) ?? {};
};

export const getUpdatedRowDataListIfTabsExist = ({
  rowDataList,
  resetRowDataList,
  activeTabIndex,
  selectedDashboardTabSectionKey,
}: {
  rowDataList: RowData[];
  resetRowDataList: RowData[];
  activeTabIndex: number;
  selectedDashboardTabSectionKey?: string;
}): RowData[] => {
  if (!selectedDashboardTabSectionKey) {
    return resetRowDataList;
  }

  const rowDataForTab =
    rowDataList?.[activeTabIndex]?.[selectedDashboardTabSectionKey];

  const resetRowDataForTab =
    resetRowDataList?.[activeTabIndex]?.[selectedDashboardTabSectionKey];

  return (rowDataForTab ?? resetRowDataForTab ?? []) as RowData[];
};

export const getRowDataFieldValue = ({
  propertyKey,
  rowData,
  propertySubKey,
  rowIndex,
  isDialogField,
  dialogRowData,
  selectedTabSectionKey,
}: {
  propertyKey: string;
  rowData: RowData;
  propertySubKey?: string;
  rowIndex?: number;
  isDialogField?: boolean;
  dialogRowData?: RowData;
  selectedTabSectionKey?: string;
}): RowDataValueType => {
  if (isDialogField) {
    return dialogRowData?.[propertyKey] ?? null;
  }

  if (selectedTabSectionKey && rowIndex !== undefined && propertySubKey) {
    const sectionData = rowData[selectedTabSectionKey] as RowData;
    const rowArray = sectionData?.[propertyKey] as RowDataObject[];
    return rowArray?.[rowIndex]?.[propertySubKey] ?? null;
  }

  if (selectedTabSectionKey) {
    const sectionData = rowData[selectedTabSectionKey] as RowData;
    return (sectionData?.[propertyKey] as PrimitiveValueType) ?? null;
  }

  if (rowIndex !== undefined && propertySubKey) {
    const rowArray = rowData?.[propertyKey] as RowDataObject[];
    return rowArray?.[rowIndex]?.[propertySubKey] ?? null;
  }

  if (propertySubKey && rowIndex === undefined) {
    const rowDataNestedObject = rowData?.[propertyKey] as RowDataObject;
    return rowDataNestedObject?.[propertySubKey] ?? null;
  }

  return (rowData[propertyKey] as PrimitiveValueType) ?? null;
};

export const getResolvedDynamicRowDataValue = ({
  propertyKey,
  dynamicField,
  rowData,
  rowIndex,
  selectedTabSectionKey,
}: {
  propertyKey: string;
  dynamicField: FilterFieldType;
  rowData: RowData;
  rowIndex?: number;
  selectedTabSectionKey?: string;
}): FilterFieldValueType => {
  if (typeof dynamicField !== "string" || !dynamicField.startsWith("$")) {
    return dynamicField;
  }

  const resolvedKey = dynamicField.slice(1); // remove "$"

  if (selectedTabSectionKey && rowIndex !== undefined) {
    const sectionData = rowData[selectedTabSectionKey] as RowData;
    const rowArray = sectionData?.[propertyKey] as RowDataObject[];
    return (
      (rowArray?.[rowIndex]?.[resolvedKey] as
        | PrimitiveValueType
        | PrimitiveValueType[]) ?? null
    );
  }

  if (selectedTabSectionKey) {
    const sectionData = rowData[selectedTabSectionKey] as RowData;
    return (sectionData?.[resolvedKey] as PrimitiveValueType) ?? null;
  }

  if (rowIndex !== undefined) {
    const rowArray = rowData[propertyKey] as RowDataObject[];
    return (
      (rowArray?.[rowIndex]?.[resolvedKey] as
        | PrimitiveValueType
        | PrimitiveValueType[]) ?? null
    );
  }

  return (rowData[resolvedKey] as PrimitiveValueType) ?? null;
};

export const dispatchUpdateField = ({
  propertyKey,
  propertySubKey,
  rowIndex,
  newFieldValue,
  targetFieldKey,
  isDialogField,
  selectedTabSectionKey,
  isForResetField,
  dispatch,
}: {
  propertyKey: string;
  propertySubKey?: string;
  rowIndex: number | undefined;
  newFieldValue: RowDataValueType;
  isDialogField?: boolean;
  targetFieldKey: string;
  selectedTabSectionKey?: string;
  isForResetField?: boolean;
  dispatch: React.Dispatch<RowDataReducerAction>;
}): void => {
  if (rowIndex !== undefined) {
    dispatch({
      type: isForResetField ? "UPDATE_RESET_FIELD" : "UPDATE_FIELD",
      payload: {
        propertyKey,
        newFieldValue,
        rowIndex,
        propertySubKey: targetFieldKey,
        isDialogField,
        selectedTabSectionKey,
      },
    });
  } else if (propertySubKey && rowIndex === undefined) {
    dispatch({
      type: isForResetField ? "UPDATE_RESET_FIELD" : "UPDATE_FIELD",
      payload: {
        propertyKey,
        propertySubKey: targetFieldKey,
        newFieldValue,
        isDialogField,
        selectedTabSectionKey,
      },
    });
  } else {
    dispatch({
      type: isForResetField ? "UPDATE_RESET_FIELD" : "UPDATE_FIELD",
      payload: {
        propertyKey: targetFieldKey,
        newFieldValue,
        isDialogField,
        selectedTabSectionKey,
      },
    });
  }
};

export const removeMetaDataFieldsFromRowData = (rowData: RowData): RowData => {
  const updatedRowdata: RowData = {};
  Object.keys(rowData).forEach((key) => {
    if (!METADATA_COLUMNS.has(key) && rowData.hasOwnProperty(key)) {
      updatedRowdata[key] = rowData[key];
    }
  });
  // storing existing `id` for API calls if needed
  updatedRowdata[TRACK_RECORD_DATA_STATE_KEYS.TRACK_EXISTING_ID] =
    rowData[ROW_DATA_KEYS.ID];
  // storing existing `isClonedRecord` if needed
  updatedRowdata[TRACK_RECORD_DATA_STATE_KEYS.TRACK_IS_CLONED_RECORD] = true;
  return updatedRowdata;
};

export const isRecordEmpty = (
  rowData: RowData,
  selectedTabSectionKey: string | undefined,
  deletedObjectsList: DeletedObjectsList,
): boolean => {
  if (selectedTabSectionKey) {
    const section = rowData[selectedTabSectionKey] ?? {};
    const arrayValues = Object.values(section).filter(Array.isArray);

    const hasDeletedRecords =
      Object.keys(deletedObjectsList[selectedTabSectionKey] ?? {}).length > 0;

    if (hasDeletedRecords || arrayValues.length === 0) return false;

    return arrayValues.every((arr) => arr.length === 0);
  }
  return Object.keys(rowData).length === 0;
};

export const getUpdatedSetupBaseRequestInfo = (
  setupBaseRequestInfo: SetupBaseRequestInfo,
  selectedTabSectionKey: string | undefined,
  selectedDashboardTabSectionKey: string | undefined,
): SetupBaseRequestInfo => {
  if (selectedTabSectionKey) {
    const mappedSetups: Record<string, string> = {
      poGroupTab: "POGroups",
      poAccountsTab: "POAccounts",
      poAddressTab: "PoAddress",
      shipViasTab: "POShipVia",
      poTypesTab: "POTypes",
    };
    return {
      ...setupBaseRequestInfo,
      functionalUnitName:
        mappedSetups[selectedTabSectionKey] ?? selectedTabSectionKey,
    };
  }

  if (selectedDashboardTabSectionKey) {
    const mappedSetups: Record<string, string> = {
      vendorCompanyTypeTab: "VendorCompanyType",
      vendorCategoryTab: "VendorCategory",
      vendorPaymentTypesTab: "VendorPaymentTypes",
    };
    return {
      ...setupBaseRequestInfo,
      functionalUnitName:
        mappedSetups[selectedDashboardTabSectionKey] ??
        selectedDashboardTabSectionKey,
    };
  }

  return setupBaseRequestInfo;
};

export const getUpdatedDeleteRowData = (
  rowData: RowData,
  setupName: string,
): RowData => {
  if (setupName == SCHEMA_TITLES.AR_PORTAL) {
    return {
      ...rowData,
      [ROW_DATA_KEYS.DELETE_DATA]: new Date().toISOString().split("T")[0],
    };
  }
  return rowData;
};

export const getRowDataObjectArray = (
  propertyKey: string,
  rowData: RowData,
  selectedTabSectionKey: string | undefined,
): RowDataObject[] => {
  return selectedTabSectionKey
    ? ((rowData[selectedTabSectionKey] as RowData)[
        propertyKey
      ] as RowDataObject[])
    : (rowData[propertyKey] as RowDataObject[]);
};

export const getRowObjectToBeDeleted = (
  propertyKey: string,
  rowIndex: number,
  rowData: RowData,
  selectedTabSectionKey: string | undefined,
): {
  currentRowObjectId: string | undefined;
  deletedObjectWithInactiveStatus: RowDataObject;
} => {
  const rowDataObjects = getRowDataObjectArray(
    propertyKey,
    rowData,
    selectedTabSectionKey,
  );

  const row = rowDataObjects[rowIndex];

  if (!row || typeof row !== "object") {
    return {
      currentRowObjectId: undefined,
      deletedObjectWithInactiveStatus: {} as RowDataObject,
    };
  }

  const currentRowObjectId = (row.id ||
    row.group_address_id ||
    row.group_company_id) as string | undefined;

  let rowWithInactiveStatus = row;
  if (currentRowObjectId) {
    rowWithInactiveStatus = {
      ...row,
      [ROW_DATA_KEYS.ACTIVE_STATUS]: "inactive",
    };
  }
  return {
    currentRowObjectId,
    deletedObjectWithInactiveStatus: rowWithInactiveStatus,
  };
};

export const dispatchDeletedObjectIfExists = ({
  propertyKey,
  propertySubKey,
  nestedRowIndex,
  index,
  rowData,
  selectedTabSectionKey,
  dispatch,
}: {
  propertyKey: string;
  propertySubKey?: string;
  nestedRowIndex?: number;
  index: number;
  rowData: RowData;
  selectedTabSectionKey?: string;
  dispatch: React.Dispatch<RowDataReducerAction>;
}): void => {
  if (nestedRowIndex !== undefined && propertySubKey) {
    const parentRows = getRowDataObjectArray(
      propertyKey,
      rowData,
      selectedTabSectionKey,
    );
    const nestedValue = parentRows[nestedRowIndex]?.[propertySubKey];
    const nestedArray = Array.isArray(nestedValue)
      ? (nestedValue as unknown as RowDataObject[])
      : [];
    const nestedObject = nestedArray[index];
    if (!nestedObject || typeof nestedObject !== "object") return;

    const currentRowObjectId = nestedObject.id as string | undefined;
    if (currentRowObjectId) {
      dispatch({
        type: "SET_DELETED_OBJECTS",
        payload: {
          propertyKey,
          propertySubKey,
          nestedRowIndex,
          newFieldValue: {
            ...nestedObject,
            [ROW_DATA_KEYS.ACTIVE_STATUS]: "inactive",
          },
          rowIndex: index,
          selectedTabSectionKey,
        },
      });
    }
    return;
  }

  const { currentRowObjectId, deletedObjectWithInactiveStatus } =
    getRowObjectToBeDeleted(propertyKey, index, rowData, selectedTabSectionKey);

  if (currentRowObjectId) {
    dispatch({
      type: "SET_DELETED_OBJECTS",
      payload: {
        propertyKey,
        newFieldValue: deletedObjectWithInactiveStatus,
        rowIndex: index,
        selectedTabSectionKey,
      },
    });
  }
};

export const dispatchDeletedObjectsForAll = ({
  propertyKey,
  propertySubKey,
  nestedRowIndex,
  rowData,
  selectedTabSectionKey,
  dispatch,
}: {
  propertyKey: string;
  propertySubKey?: string;
  nestedRowIndex?: number;
  rowData: RowData;
  selectedTabSectionKey?: string;
  dispatch: React.Dispatch<RowDataReducerAction>;
}): void => {
  if (nestedRowIndex !== undefined && propertySubKey) {
    const parentRows = getRowDataObjectArray(
      propertyKey,
      rowData,
      selectedTabSectionKey,
    );
    const nestedValue = parentRows[nestedRowIndex]?.[propertySubKey];
    const nestedArray = Array.isArray(nestedValue)
      ? (nestedValue as unknown as RowDataObject[])
      : [];

    nestedArray.forEach((item, index) => {
      if (!item || typeof item !== "object") return;
      const currentRowObjectId = item?.id as string | undefined;
      if (currentRowObjectId) {
        dispatch({
          type: "SET_DELETED_OBJECTS",
          payload: {
            propertyKey,
            propertySubKey,
            nestedRowIndex,
            newFieldValue: {
              ...item,
              [ROW_DATA_KEYS.ACTIVE_STATUS]: "inactive",
            },
            rowIndex: index,
            selectedTabSectionKey,
          },
        });
      }
    });
    return;
  }

  const rowDataPropertyItems = getRowDataObjectArray(
    propertyKey,
    rowData,
    selectedTabSectionKey,
  );

  rowDataPropertyItems.forEach((_, index) => {
    const { currentRowObjectId, deletedObjectWithInactiveStatus } =
      getRowObjectToBeDeleted(
        propertyKey,
        index,
        rowData,
        selectedTabSectionKey,
      );

    if (currentRowObjectId) {
      dispatch({
        type: "SET_DELETED_OBJECTS",
        payload: {
          propertyKey,
          newFieldValue: deletedObjectWithInactiveStatus,
          rowIndex: index,
          selectedTabSectionKey,
        },
      });
    }
  });
};
